import toast, { Toaster, useToasterStore } from 'react-hot-toast'
import NotificationHomeWrapper from './notifications/components/NotificationsHomeWrapper'
import React, { useEffect, useMemo, useCallback } from 'react'
import { hexToRGBA } from './notifications/helpers/hextoRGBA'
import { useTheme, ThemeContext } from './hooks/useTheme'
import './lib/goober-setup'

// Constants moved outside component to avoid recreation on each render
const TEAL_PALETTE = {
  50: '#e6f2f2',
  100: '#b0d8d8',
  200: '#8ac5c5',
  300: '#54aaaa',
  400: '#339999',
  500: '#008080',
  600: '#007474',
  700: '#005b5b',
  800: '#004646',
  900: '#003636'
}

const LIGHT_COLOR = '53,65,63'
const DARK_COLOR = '227, 252, 251'

export const FynoInappCenter = (props) => {
  const { mode, themeConfig, notificationSettings } = props

  const mainColor =
    mode === 'light'
      ? themeConfig?.lightColor || LIGHT_COLOR
      : themeConfig?.darkColor || DARK_COLOR

  const BREAKPOINT_VALUES = {
    xs: '0px',
    sm: '600px',
    md: '900px',
    lg: '1200px',
    xl: '1536px'
  }

  const getBreakpointUp = useCallback((key) => {
    return `@media (min-width: ${BREAKPOINT_VALUES[key]})`
  }, [])

  const getBreakpointDown = useCallback((key) => {
    return `@media (max-width: ${BREAKPOINT_VALUES[key]})`
  }, [])

  const primaryToastColor =
    mode === 'light'
      ? notificationSettings?.invertTheme
        ? '#F2F2F2'
        : '#1A1A1A'
      : notificationSettings?.invertTheme
      ? '#1A1A1A'
      : '#F2F2F2'

  const secondaryToastColor =
    mode === 'light'
      ? notificationSettings?.invertTheme
        ? '#B2B2B2'
        : '#595959'
      : notificationSettings?.invertTheme
      ? '#595959'
      : '#B2B2B2'

  const theme = useMemo(
    () => ({
      mode: mode,
      typography: {
        fontFamily: themeConfig?.font ?? 'Inter, sans-serif'
      },
      breakpoints: {
        up: getBreakpointUp,
        down: getBreakpointDown
      },
      palette: {
        common: {
          black: '#000',
          white: '#FFF'
        },
        mode: mode,
        primary: {
          main:
            themeConfig?.primary ??
            (mode === 'light' ? TEAL_PALETTE['500'] : '#18A5A5'),
          contrastText: '#FFF'
        },
        secondary: {
          main:
            themeConfig?.secondary ??
            (mode === 'light' ? '#5B5B5B' : '#B2B2B2'),
          contrastText: '#FFF'
        },
        error: {
          light: mode === 'light' ? '#EF756A' : '#EC5A4D',
          main: mode === 'light' ? '#E73121' : '#D22D1E',
          dark: mode === 'light' ? '#A42317' : '#7F1B12',
          contrastText: '#FFF'
        },
        text: {
          primary: mode === 'light' ? '#1A1A1A' : '#F2F2F2',
          secondary: mode === 'light' ? '#595959' : '#B2B2B2',
          disabled: mode === 'light' ? '#A6A6A6' : '#737373',
          border: mode === 'light' ? '#00000033' : '#FFFFFF4D'
        },
        toasttext: {
          primary: primaryToastColor,
          secondary: secondaryToastColor,
          disabled: mode === 'light' ? '#A6A6A6' : '#737373',
          border: mode === 'light' ? '#00000033' : '#FFFFFF4D'
        },
        divider: `rgba(${mainColor}, 0.12)`,
        pin: {
          color:
            themeConfig?.pinColor ?? mode === 'light' ? '#1962CF' : '#6596DF',
          background: mode === 'light' ? '#dbdbdb' : '#7d7c7c'
        },
        background: {
          paper:
            mode === 'light'
              ? themeConfig?.lightBackground || '#FCFCFC'
              : themeConfig?.darkBackground || '#1a1a1a',
          inverted:
            mode === 'dark'
              ? themeConfig?.lightBackground || '#FCFCFC'
              : themeConfig?.darkBackground || '#1a1a1a',
          default: mode === 'light' ? '#F2F2F2' : '#0D0D0D',

          configBackground:
            mode === 'light'
              ? themeConfig?.lightConfigBackground || '#FCFCFC'
              : themeConfig?.darkConfigBackground || '#1a1a1a',
          configSection:
            mode === 'light'
              ? themeConfig?.lightConfigSectionBackground || '#FCFCFC'
              : themeConfig?.darkConfigSectionBackground || '#0D0D0D'
        },
        inverted: {
          paper:
            mode === 'dark'
              ? themeConfig?.lightBackground || '#FCFCFC'
              : themeConfig?.darkBackground || '#1a1a1a',
          default: mode === 'light' ? '#F2F2F2' : '#0D0D0D'
        },
        action: {
          active: `rgba(${mainColor}, 0.54)`,
          hover: `rgba(${mainColor}, 0.04)`,
          selected: `rgba(${mainColor}, 0.08)`,
          disabled: `rgba(${mainColor}, 0.3)`,
          focus: `rgba(${mainColor}, 0.12)`
        },
        chip: {
          background: themeConfig?.primary
            ? `${hexToRGBA(themeConfig?.primary, 0.8)}`
            : hexToRGBA(mode === 'light' ? TEAL_PALETTE['500'] : '#18A5A5', 0.8)
        }
      }
    }),
    [
      mode,
      themeConfig,
      notificationSettings,
      mainColor,
      getBreakpointUp,
      getBreakpointDown
    ]
  )
  const { toasts } = useToasterStore()

  useEffect(() => {
    toasts
      .filter((t) => t.visible)
      .filter((_, i) => i >= (notificationSettings?.maxToastCount || 3))
      .forEach((t) => toast.dismiss(t.id))
  }, [toasts, notificationSettings?.maxToastCount])

  return (
    <ThemeContext.Provider value={theme}>
      <NotificationHomeWrapper {...props} />
      <Toaster
        containerClassName='react-hot-toast'
        position={notificationSettings?.toastPosition || 'top-right'}
        toastOptions={{
          duration: notificationSettings?.duration || 5000,
          style: {
            background: notificationSettings?.invertTheme
              ? theme.palette.inverted.paper
              : theme.palette.background.paper,
            color: notificationSettings?.invertTheme
              ? theme.palette.inverted.paper
              : theme.palette.background.paper
          }
        }}
      />
    </ThemeContext.Provider>
  )
}

export const FynoToast = (config) => {
  const { toasts } = useToasterStore()
  const { maxToastCount = 3 } = config

  useEffect(() => {
    toasts
      .filter((t) => t.visible)
      .filter((_, i) => i >= maxToastCount)
      .forEach((t) => toast.dismiss(t.id))
  }, [toasts, maxToastCount])

  return <Toaster {...config} reverseOrder={false} />
}

// Export useTheme for external use
export { useTheme }
